import { useState } from "react";
import AuthLayout from "./AuthLayout";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useLocation } from "react-router-dom";
import Mobile2FactorIcon from "../../assets/auth/mobile2Factor.svg";
import { ref, set } from "firebase/database";
import { database } from "../../config/firebase";
import { getAuth } from "firebase/auth";
import { useAppSelector } from "../../store/hooks";
import { ArrowLeft } from "lucide-react";

const Mobile2FactoreVerification = () => {
  const auth = getAuth();
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const location = useLocation();

  // Prefill phone from navigation state if available
  const initialPhone = location.state?.phoneNumber || "";
  const [phone, setPhone] = useState(initialPhone);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Ensure phone number starts with + and country code
    let formattedNumber = phoneNumber;
    if (!formattedNumber.startsWith("+")) {
      formattedNumber = "+" + formattedNumber;
    }
    return formattedNumber;
  };
  const validatePhoneNumber = (phoneNumber: string) => {
    // Regular expression to validate phone numbers for most countries
    const regex = /^\+?(\d{1,4})?[-.\s]?(\d{1,15})$/;

    // Match the phone number against the regex
    return regex.test(phoneNumber);
  };
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (value && !validatePhoneNumber(value)) {
      setError("Please enter a valid 10-digit phone number");
    } else {
      setError("");
    }
  };
  const handleSubmit = async () => {
    if (!phone) {
      setError("Please enter a valid phone number.");
      return;
    }
    setLoading(true);
    const formattedPhone = formatPhoneNumber(phone);
    const firebaseUser = auth.currentUser;

    if (!firebaseUser && !user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    const userId = firebaseUser?.uid || user?.id;
    if (!userId) {
      setError("User ID not available");
      setLoading(false);
      return;
    }

    const functionUrl = `https://us-central1-supporty-staging.cloudfunctions.net/sendOTP`;
    try {
      const userSettingsRef = ref(database, `users/${userId}/setting`);
      await set(userSettingsRef, {
        phoneNumber: formattedPhone,
        SMS: true,
      });
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phNumber: formattedPhone, type: "SMS" }),
      });

      const result = await response.json();
      if (result.success) {
        sessionStorage.setItem("verificationPhone", formattedPhone);
        navigate("/signup/otp-verify", {
          state: {
            phoneNumber: formattedPhone,
            type: "SMS",
          },
        });
      } else {
        setError(result.error || "Failed to send verification code.");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex items-center justify-center bg-white">
        <div className="flex-1 flex flex-col justify-center p-8 items-center mt-12">
          <div className="">
            <div className="text-center">
              <img
                src={Mobile2FactorIcon}
                alt="Help Illustration"
                className="mx-auto"
              />

              <div className="flex justify-center items-center gap-5 mt-6">
                <button onClick={() => navigate(-1)}>
                  <ArrowLeft
                    className="h-[48px] w-[48px] text-[#919191]"
                  />
                </button>
                <h2 className="text-title nunito-extrabold text-[#0A0A0A] text-[24px] w-[90%]">
                  Enter your mobile number
                </h2>
              </div>

              <p
                className="mt-2 text-center nunito-regular max-w-[17rem] mx-auto"
                style={{
                  color: "#696969",
                }}
              >
                Enter your mobile number below, and we’ll send you a 2FA code
              </p>
            </div>
            <div className="flex justify-center gap-1 ml-[29%] mt-10 flex-col">
              <label className="text-[#1D1D1D] nunito-semibold">
                Phone Number
              </label>
              <PhoneInput
                country={"us"}
                enableLongNumbers={true}
                value={phone}
                onChange={handlePhoneChange}
                containerClass="w-full nunito-medium"
                inputClass={`w-full nunito-medium h-12 !px-12 !py-2 text-lg ${
                  error
                    ? "!border-[#F55A51] !border-[1px] !border !text-[#F55A51]"
                    : "border-[#E5E7EB]"
                }`}
                // buttonClass={
                //   error ? "!border-[#F55A51] !border-2 !border" : "border-[#E5E7EB]"
                // }
                searchStyle={{ width: "100%" }}
                dropdownStyle={{ width: "300px" }}
                specialLabel="Phone Number"
                inputProps={{
                  placeholder: "97292788",
                  name: "phone",
                }}
              />
            </div>
            {error && (
              <p className=" text-[#F55A51] text-sm text-center mt-1">
                {error}
              </p>
            )}
          </div>
          <div className="flex flex-col items-center mt-6 ml-4">
            <button
              type="button"
              onClick={handleSubmit}
              className={`mt-2 w-[270px] flex items-center justify-center px-4 py-2 text-white font-medium rounded-md ${"bg-[#F15D18] hover:bg-primary"} focus:ring-2 focus:ring-orange-500`}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Mobile2FactoreVerification;
